// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xngGaGeneralEventEvent from "@guestsupply/App/Handlers/GoogleAnalytics/GeneralEvents/GeneralEvents";
import loadAccountTilesData from "@guestsupply/App/Handlers/XNG/AccountTilesData";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { LazyImageProps } from "@guestsupply/MobiusOverrides/LazyImage";
import LazyImage from "@guestsupply/MobiusOverrides/LazyImage/LazyImage";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const session = getSession(state);
    const wishListId = session.properties.orderFormId;
    const listDetailsPageLink = getPageLinkByPageType(state, "MyListsDetailsPage")?.url;
    const listsPageLink = getPageLinkByPageType(state, "MyListsPage")?.url;
    return {
        accountTilesData: state.xAccountTilesData?.data,
        session,
        billToState: getCurrentBillToState(state),
        isAuthenticated: session.isAuthenticated && !session.isGuest,
        wishListId,
        listDetailsPageLink,
        listsPageLink,
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
    };
};

const mapDispatchToProps = {
    loadAccountTilesData,
    loadCurrentBillTo,
    // xng-change: add google event
    xngGaGeneralEventEvent,
};
enum fields {
    itemsList = "itemsList",
}
const enum tilesFields {
    type = "type",
    title = "title",
    link = "link",
    image = "image",
}
interface AccountTilesListProps {
    fields: {
        [tilesFields.type]: string;
        [tilesFields.title]: string;
        [tilesFields.link]: LinkFieldValue;
        [tilesFields.image]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.itemsList]: AccountTilesListProps[];
    };
}

enum expand {
    quotesCount = "quotes",
    wishListsCount = "lists",
    orderHistoryCount = "orderHistory",
    orderApprovalCount = "orderApproval",
    savedOrdersCount = "savedOrders",
    invoiceHistoryCount = "invoiceHistory",
    backOrdersCount = "backOrder",
    autoDeliveryCount = "autoDelivery",
}

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface AccountTilesStyles {
    wrapper?: InjectableCss;
    infoSection?: InjectableCss;
    titleSection?: InjectableCss;
    title?: TypographyProps;
    customerInfoWrapper?: InjectableCss;
    customerInfoTitle?: TypographyProps;
    customerInfoValue?: TypographyProps;
    customerInfo?: InjectableCss;
    blockContent?: InjectableCss;
    item?: {
        title?: TypographyProps;
        imageContainer?: InjectableCss;
        link?: LinkPresentationProps;
        number?: TypographyProps;
        image?: LazyImageProps;
        imageWrapper?: InjectableCss;
    };
    link?: LinkPresentationProps;
    titleWrapper?: InjectableCss;
    blocksGridContainer?: GridContainerProps;
    blockGridItem?: GridItemProps;
    greetingTitle?: TypographyProps;
    clickable?: ClickablePresentationProps;
    SmallHeadingAndText?: SmallHeadingAndTextStyles;
    spinnerWrapper?: InjectableCss;
}

export const accountTilesStyles: AccountTilesStyles = {};

const styles = accountTilesStyles;

const AccountTiles: FC<Props> = ({
    fields,
    session,
    billToState,
    accountTilesData,
    loadAccountTilesData,
    isAuthenticated,
    loadCurrentBillTo,
    wishListId,
    listDetailsPageLink,
    listsPageLink,
    // xng-change: add google event
    xGoogleAnalytics,
    xngGaGeneralEventEvent,
}) => {
    if (!isAuthenticated) {
        return null;
    }

    const billTo = billToState.value;

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading) {
            loadCurrentBillTo();
        }
    }, []);

    useEffect(() => {
        if (!accountTilesData || !Object.keys(accountTilesData).length) {
            loadAccountTilesData({
                expand: fields.itemsList.map(item => expand[item.fields.type as keyof typeof expand]),
            });
        }
    }, [fields.itemsList]);

    const itemsLinks = useGetLinks(fields.itemsList, o => o.fields.link);
    // XNG-CHANGES: GUEST-2659
    const orderGuideUrl = wishListId ? `${listDetailsPageLink}?id=${wishListId}` : `${listsPageLink}`;

    if (!billTo || !session) {
        return (
            <StyledWrapper {...styles.spinnerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="AccountTiles">
            <StyledWrapper {...styles.infoSection}>
                <StyledWrapper {...styles.titleSection}>
                    <Typography {...styles.greetingTitle} data-test-selector="AccountTiles_title">
                        {siteMessage("Custom_AccountTiles_Greeting")}
                        {session.firstName && ","}
                    </Typography>
                    {session.firstName && (
                        <Typography {...styles.title} data-test-selector="AccountTiles_subtitle">
                            {session.firstName}!
                        </Typography>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.customerInfoWrapper}>
                    <StyledWrapper {...styles.customerInfo}>
                        <SmallHeadingAndText
                            heading={translate("Company Name")}
                            text={`${billTo?.companyName}`}
                            extendedStyles={styles.SmallHeadingAndText}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.customerInfo}>
                        <SmallHeadingAndText
                            heading={translate("Account Number")}
                            text={`${billTo?.customerNumber}`}
                            extendedStyles={styles.SmallHeadingAndText}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <GridContainer {...styles.blocksGridContainer}>
                {fields.itemsList?.map((itemField, index) => {
                    const item = {
                        link: itemsLinks?.[index].url || "",
                        type: itemField.fields.type,
                        title: itemField.fields.title,
                        image: itemField.fields.image,
                    };
                    return (
                        <GridItem {...styles.blockGridItem} key={item.type}>
                            <Clickable
                                // XNG-CHANGES: GUEST-2659
                                href={item.type === "orderGuide" ? orderGuideUrl : item.link}
                                // xng-change: add google event
                                onClick={() =>
                                    xngGaGeneralEventEvent({
                                        eventType: GOOGLE_EVENTS.MY_ACCOUNT_SHORTCUT_EVENT,
                                        includeEvent: xGoogleAnalytics.includeMyAccountShortcutEvent,
                                        clickText: item.title,
                                        destination: item.type === "orderGuide" ? orderGuideUrl : item.link,
                                    })
                                }
                                {...styles.clickable}
                            >
                                <VisuallyHidden as="label">{item.title}</VisuallyHidden>
                                <StyledWrapper {...styles.blockContent}>
                                    <StyledWrapper {...styles.item?.imageContainer}>
                                        <StyledWrapper {...styles.item?.imageWrapper}>
                                            <LazyImage
                                                width="35"
                                                height="35"
                                                {...styles.item?.image}
                                                src={item.image}
                                                altText={`${item.title} - ${item.type}`}
                                                customTransformations={{ default: "GUEST_Product_45x45" }}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.titleWrapper}>
                                        {accountTilesData?.[item.type as keyof typeof accountTilesData] !== null && (
                                            <Typography {...styles.item?.number}>
                                                {accountTilesData?.[item.type as keyof typeof accountTilesData]}
                                            </Typography>
                                        )}
                                        <Typography {...styles.item?.title}>{item.title}</Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </Clickable>
                        </GridItem>
                    );
                })}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountTiles),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.itemsList,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => !item.fields.isAuthenticated,
                fieldDefinitions: [
                    {
                        name: tilesFields.type,
                        displayName: "Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Quotes", value: "quotesCount" },
                            { displayName: "Lists", value: "wishListsCount" },
                            { displayName: "User Administration", value: "userAdministration" },
                            { displayName: "Order Approval", value: "orderApprovalCount" },
                            { displayName: "Saved Orders", value: "savedOrdersCount" },
                            { displayName: "My Saved Payments", value: "mySavedPayments" },
                            { displayName: "Quick Order", value: "quickOrder" },
                            { displayName: "Invoice History", value: "invoiceHistoryCount" },
                            { displayName: "Backorder Management", value: "backOrdersCount" },
                            { displayName: "Auto Delivery", value: "autoDeliveryCount" },
                            { displayName: "Order History", value: "orderHistoryCount" },
                            { displayName: "Payment Portal", value: "paymentPortal" },
                            { displayName: "Order Guide", value: "orderGuide" },
                        ],

                        hideEmptyOption: true,
                        defaultValue: "",
                    },
                    {
                        name: tilesFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: tilesFields.link,
                        displayName: "Destination",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                        allowCategories: item => false,
                        isVisible: widget => widget.fields.type !== "orderGuide",
                    },
                    {
                        name: tilesFields.image,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
